import React from "react";
import type { BCPSectionConfig } from "./bcpFieldConfig";

export const RESTORE_POLICY_SECTIONS: BCPSectionConfig[] = [
  {
    id: "basic_info",
    title: "基本情報",
    fields: [
      {
        id: "company_info",
        label: "企業の基本情報",
        type: "textarea",
        minHeight: "medium",
        placeholder: "企業名：\n所在地：\n従業員数：\n事業内容：",
        aiConsult: false,
        required: true,
      },
    ],
  },
  {
    id: "bcp_goals",
    title: "1. BCP目標設定",
    fields: [
      {
        id: "disaster_risks",
        label: "1-1 想定されるリスク",
        type: "textarea",
        minHeight: "large",
        defaultValue:
          "当社所在地で発生しうる災害リスク（ハザードマップより）\n\n地震：可能性 {高中低}\n　被害想定：{内容}\n\n津波：可能性 {高中低}\n　被害想定：{内容}\n\n洪水・高潮： 可能性 {高中低}\n　被害想定：{内容}\n\n土砂災害（地滑り等）： 可能性 {高中低}\n　被害想定：{内容}\n\n液状化：可能性 {高中低}\n　被害想定：{内容}",
        aiConsult: true,
      },
      {
        id: "important_business",
        label: "1-2 重要事業と目標復旧時間（RTO: Recovery Time Objective）",
        type: "textarea",
        minHeight: "large",
        defaultValue:
          "【重要事業1：{事業名}】\n理由：\n\n・顧客への迷惑（影響）が出るまでの期間：\n・社会全体への影響が出るまでの期間：\n・顧客離れが発生するまでの期間：\n・財務への影響（資金枯渇など）が出るまでの期間：\n\n⇒上記を踏まえた目標復旧時間（RTO）：\n\n【重要事業2：{事業名}】\n理由：\n\n・顧客への迷惑（影響）が出るまでの期間：\n・社会全体への影響が出るまでの期間：\n・顧客離れが発生するまでの期間：\n・財務への影響（資金枯渇など）が出るまでの期間：\n\n⇒上記を踏まえた目標復旧時間（RTO）：",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                重要事業と目標復旧時間の考え方
              </p>
              事業を中断すると最も影響が大きい業務を特定し、目標復旧時間を設定してください。
              {"\n\n"}【重要事業特定の観点】
              {"\n"}・売上や利益への貢献度
              {"\n"}・顧客や社会からの信頼・評判への影響
              {"\n"}・法的義務・契約上の責務
              {"\n"}・他の業務への波及効果
              {"\n\n"}【目標復旧時間の設定】
              {"\n"}・各影響が出るまでの期間を評価
              {"\n"}・最も短い期間を基準にRTOを設定
            </div>
          ),
          example: (
            <div>
              <p
                className="whitespace-pre-wrap"
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  width: '100%',
                  maxWidth: '1151px',
                  minHeight: '326px',
                }}
              >
                【重要事業1：電子部品の受託製造】
                {"\n"}理由：主要顧客である大手電機メーカーへの納品が停止すると、顧客の生産ラインに直接影響を及ぼし、契約上の損害賠償責任が発生する。また、当社の売上の60%を占める主力事業である。
                {"\n\n"}・顧客への迷惑（影響）が出るまでの期間：3日以内
                {"\n"}・社会全体への影響が出るまでの期間：1週間以内
                {"\n"}・顧客離れが発生するまでの期間：1週間以内
                {"\n"}・財務への影響（資金枯渇など）が出るまでの期間：2週間以内
                {"\n\n"}⇒上記を踏まえた目標復旧時間（RTO）：3日以内
              </div>
            </div>
          ),
        },
        aiConsult: true,
        required: true,
      },
    ],
  },
  {
    id: "resource_strategy",
    title: "2. 経営資源と現地復旧戦略整理",
    fields: [
      {
        id: "human_resources",
        label: "2-1 重要事業の実施に必要な人的リソース",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        aiConsult: true,
      },
      {
        id: "facility_recovery",
        label: "2-2 重要事業の実施に必要な設備・インフラ",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        aiConsult: true,
      },
      {
        id: "it_recovery",
        label: "2-3 重要事業の実施に必要なシステム",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        aiConsult: true,
      },
      {
        id: "supply_chain",
        label: "2-4 重要事業の実施に必要な委託先／関係先",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}\n\n【重要事業2：{事業名}】\n・{リソース1}\n・{リソース2}\n・{リソース3}\n※上記の資源が使用できない場合の実施可能な代替策：{内容}\n※上記の資源を使用できるように平時から実施する回避策：{内容}",
        aiConsult: true,
      },
      {
        id: "financial_plan",
        label: "2-5 事業継続のための財務計画",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【財務状況の把握】\n・資金繰りを定期的に確認し、緊急時に利用可能な資金を確保する。\n\n【資金調達先】\n・地元金融機関、取引銀行、政府系金融機関（日本政策金融公庫など）との連携を強化し、緊急時に迅速な資金調達ができる体制を整備する。\n\n【相談先】\n・地元商工会議所、金融機関担当者、専門コンサルタントを活用し、資金計画を定期的に見直す。",
        aiConsult: true,
      },
    ],
  },
  {
    id: "alternative_strategy",
    title: "3. 現地復旧戦略以外の戦略",
    fields: [
      {
        id: "backup_location",
        label: "3-1 バックアップ拠点の確保",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "remote_work",
        label: "3-2 業務のリモート対応計画",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "external_partners",
        label: "3-3 外部業者・パートナーとの提携",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【重要事業1：{事業名}】\n{具体的な内容}\n\n【重要事業2：{事業名}】\n{具体的な内容}",
        aiConsult: true,
      },
      {
        id: "inventory_utilization",
        label: "3-4 在庫活用",
        type: "textarea",
        minHeight: "medium",
        defaultValue:
          "【在庫活用】\n・重要製品の供給を継続するため、在庫を事前に積み増ししておく。\n・複数の倉庫に分散保管し、リスクを低減する。\n\n※在庫活用が該当しない事業の場合は、記載不要です。",
        guide: {
          content: (
            <div className="whitespace-pre-wrap">
              <p
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                代替策の考え方
              </p>
              <div style={{ color: '#4B5563' }}>
                重要事業において現地復旧戦略ではBCP目標が達成できない場合に、とりうる現地復旧戦略以外の戦略を策定してください。
                {"\n"}
                戦略の例として「代替戦略（他拠点）」「在庫活用」「外部委託」「外部購入」「撤退・縮小」などがあります。
              </div>
            </div>
          ),
          example: (
            <div>
              <p
                className="whitespace-pre-wrap"
                style={{
                  color: '#0D357F',
                  fontWeight: 600,
                  width: '100%',
                  maxWidth: '1151px',
                  height: '20px',
                  lineHeight: '20px',
                }}
              >
                記載例
              </p>
              <div
                className="whitespace-pre-wrap"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '4px',
                  border: '1px solid #D1D5DB',
                  padding: '13px',
                  width: '100%',
                  maxWidth: '1151px',
                  minHeight: '360px',
                  color: '#4B5563',
                }}
              >
                【代替拠点】
                {"\n"}・主要拠点の復旧に時間を要する場合、別拠点で同等の業務を実施する。
                {"\n"}・流用可能な設備はそのまま使用し、特有の設備は必要に応じて移設・手配する。
                {"\n\n"}【在庫活用】
                {"\n"}・重要製品の供給を継続するため、在庫を事前に積み増ししておく。
                {"\n"}・複数の倉庫に分散保管し、リスクを低減する。
                {"\n\n"}【外部委託】
                {"\n"}・有事には外部の協力会社に生産・業務を委託する。
                {"\n"}・事前協定を結び、必要に応じて自社スタッフを派遣して技術・運用面を支援する。
                {"\n\n"}【在宅勤務体制】
                {"\n"}・管理部門が災害時でも業務を継続できるよう、在宅勤務環境を整備する。
                {"\n"}・VPNやクラウドサービスなどのITインフラを導入する。
              </div>
            </div>
          ),
        },
        aiConsult: true,
      },
    ],
  },
];

export type RestorePolicyFieldId =
  (typeof RESTORE_POLICY_SECTIONS)[number]["fields"][number]["id"];
